// eoc-fetch is already being used in eoc resources, so there is no reason not
// to override the whitelist here.

// eslint-disable-next-line jira/import-whitelist
import { eocFetch as fetch } from '@atlassian/eoc-fetch/controllers';
import { getReroutableURL } from '@atlassian/jira-fetch/src/utils/get-reroutable-url.tsx';
import { createResource, type RouteResource } from '@atlassian/react-resource-router';
import getPillarLocalDevelopment from '@jira/ssr__global-build-context/src/getPillarLocalDevelopment.tsx';
import { CONSOLIDATION_STATE_RESOURCE_TYPE } from './consolidationStateResourceType.constants.tsx';
import {
	type TenantInfo,
	type TenantInfoClientResponse,
	type TenantInfoServerResponse,
	ConsolidationState,
} from './TenantInfo.types.tsx';

/**
 * Does the request to the internal OpsGenie gateway if run on the server.
 * On the client, will do a regular request with the cloudid prefix.
 */
export const fetchConsolidationState = (
	cloudId: string,
	atlassianAccountId: string | null,
	isAnonymous: boolean,
	appPermissions?: {
		hasServiceDeskAccess: boolean;
	},
): Promise<TenantInfo> => {
	let url = '/internal/v1/users/ops-user-info';
	if (cloudId) {
		url = `${url}/${cloudId}`;
	} else {
		throw new Error('No tenant info exists.');
	}

	let promise: Promise<TenantInfo>;

	if ((appPermissions && appPermissions.hasServiceDeskAccess === false) || isAnonymous) {
		promise = new Promise((resolve) => {
			resolve({
				consolidationState: ConsolidationState.NOT_CONSOLIDATED,
				isAdmin: false,
			});
		});
		return promise;
	}

	url = `${url}/${atlassianAccountId}`;

	if (__SERVER__ && !getPillarLocalDevelopment()) {
		promise = fetch<TenantInfoServerResponse>(url, {
			exactUrl: true,
			headers: {
				'x-ssr-service-name': 'og-apps-user-int-api',
			},
		});
	} else {
		const clientUrl = '/v1/users/ops-user-info';
		promise = fetch<TenantInfoClientResponse>(
			getReroutableURL(`/gateway/api/jsm/ops/web/${cloudId}${clientUrl}`),
			{ exactUrl: true },
		).then(({ data }) => data);
	}

	return promise;
};

const getConsolidationStateResourceFactory = (forceBrowser = false) =>
	createResource<TenantInfo>({
		type: CONSOLIDATION_STATE_RESOURCE_TYPE,
		getKey: () => 'tenantInformation',
		getData: (_, { tenantContext: { cloudId, appPermissions, atlassianAccountId, isAnonymous } }) =>
			fetchConsolidationState(cloudId, atlassianAccountId, isAnonymous, appPermissions),
		maxAge: Infinity,
		isBrowserOnly: forceBrowser,
		isCritical: true,
	});

const consolidationResourceForceBrowser = getConsolidationStateResourceFactory(true);
const consolidationResourceDontForceBrowser = getConsolidationStateResourceFactory(false);

/**
 * This is SSR-enabled and will do the request on SSR whenever possible.
 * To disable this behaviour, provide the optional parameter as true.
 * @deprecated This resource is deprecated to use as parameter to useResource, only usage should be adding to resource property of a route. Please migrate to useOgTenantInfo hook.
 */
export const getConsolidationStateResource = (forceBrowser = false): RouteResource<TenantInfo> => {
	/**
	 * This needs to return the same resource instance each time (based on args) or else we end up with many unnecessary re-renders
	 */
	return forceBrowser ? consolidationResourceForceBrowser : consolidationResourceDontForceBrowser;
};
